import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Drawer,
  Divider,
  Typography,
} from "@mui/material";
import { useConciergeStore, ensureConciergeSocket } from "../store/conciergeStore";
import { markSeen as apiMarkSeen, markRead as apiMarkRead } from "../api/conciergeApi";
import ConciergeRoom from "../components/Concierge/ConciergeRoom";
import ConciergeThreadList from "../components/Concierge/ConciergeThreadList";
import SidePanel from "../components/Concierge/SidePanel";
import ConciergeNavRail from "../components/Concierge/ConciergeNavRail";
import SettingsList from "../components/Concierge/Setting/SettingsList";

// Debug helper for pretty logs
const dbg = (...args) => {
  try { console.debug('[ConciergeCenter]', ...args); } catch {}
};

const drawerWidth = 300;

// Resolve the acting staff user for accurate read receipts
function resolveStaffActor() {
  try {
    const st = useConciergeStore.getState();
    const me = st?.me;
    const id = me?._id || me?.id || (typeof window !== 'undefined' && (window.__STAFF_ID__ || window.__STAFF__?.id || window.__STAFF__?._id)) || (typeof localStorage !== 'undefined' && (localStorage.getItem('staffId') || localStorage.getItem('userId')));
    return id ? { role: 'staff', id: String(id) } : { role: 'staff' };
  } catch (_) {
    return { role: 'staff' };
  }
}

// Pick the latest guest message id that isn't marked seen by staff yet
function pickLastGuestUnseenId(pid, uid) {
  try {
    const state = useConciergeStore.getState();
    const key = `concierge:${String(pid)}:${String(uid)}`;
    const list = (state.messagesByThread && state.messagesByThread[key]) || [];
    for (let i = list.length - 1; i >= 0; i--) {
      const m = list[i];
      if (!m) continue;
      const role = (m.sender && (m.sender.role || m.sender)) || m.role;
      const isGuest = role === 'guest';
      const seenByStaff = Array.isArray(m.seenBy) && m.seenBy.some(a => a && a.role === 'staff');
      if (isGuest && !seenByStaff) return m._id || m.id || null;
    }
  } catch {}
  return null;
}

export default function ConciergeCenter() {
  const propertyId = localStorage.getItem("activePropertyId");

  dbg('mount', { propertyId });

  const activeThread = useConciergeStore((state) => state.activeThread);
  const setActiveThread = useConciergeStore((state) => state.setActiveThread);
  const fetchThreads = useConciergeStore((state) => state.fetchThreads);
  const threads = useConciergeStore((state) => state.threads);
  const joinThreadRoom = useConciergeStore((state) => state.joinThreadRoom);
  const leaveThreadRoom = useConciergeStore((state) => state.leaveThreadRoom);

  // derive current messages length for active thread to re-arm observer when new messages arrive
  const messagesCount = useConciergeStore((s) => {
    const pid = String(activeThread?.propertyId || propertyId || '');
    const uid = activeThread?.userUid || (activeThread?.id ? String(activeThread.id).split(':').pop() : undefined);
    if (!pid || !uid) return 0;
    const key = `concierge:${pid}:${uid}`;
    return (s.messagesByThread?.[key]?.length) || 0;
  });

  // Expose inspector on window for debugging
  useEffect(() => {
    // expose helpers for quick inspection from DevTools
    window.debugConcierge = {
      getState: useConciergeStore.getState,
      dump() {
        const s = useConciergeStore.getState();
        return {
          activeThread: s.activeThread,
          threadsKeys: Object.keys(s.threads || {}),
          messagesByThreadKeys: Object.keys(s.messagesByThread || {}),
        };
      },
    };
    dbg('debugConcierge ready');
  }, []);

  // Ensure socket is ready once
  useEffect(() => {
    dbg('ensureConciergeSocket()');
    ensureConciergeSocket();
  }, []);

  // Load threads when property changes
  useEffect(() => {
    if (!propertyId) return;
    dbg('fetchThreads ->', propertyId);
    const p = fetchThreads(propertyId);
    if (p && typeof p.then === 'function') {
      p.then(() => {
        const s = useConciergeStore.getState();
        dbg('fetchThreads resolved', { keys: Object.keys(s.threads || {}) });
      }).catch((e) => dbg('fetchThreads error', e));
    }
  }, [propertyId, fetchThreads]);

  const threadList = useMemo(() => Object.values(threads || {}), [threads]);

  useEffect(() => {
    dbg('threadList len', threadList.length);
  }, [threadList]);

  // Auto-select the concierge thread for this property when nothing is active
  useEffect(() => {
    dbg('autoSelect check', { hasActive: !!activeThread, len: threadList.length, propertyId });
    if (!activeThread && threadList.length > 0) {
      const pid = String(propertyId || "");
      const preferred = threadList.find((t) => (t.kind === "concierge" && (String(t.propertyId) === pid || String(t.id || "").includes(pid)))) || threadList[0];
      if (preferred) {
        dbg('autoSelect choosing', { id: preferred.id, kind: preferred.kind, userUid: preferred.userUid, propertyId: String(preferred.propertyId) });
        setActiveThread(preferred);
      }
    }
  }, [activeThread, threadList, propertyId, setActiveThread]);

  // Join the correct room for live updates when active thread changes
  useEffect(() => {
    dbg('activeThread changed', { id: activeThread?.id, userUid: activeThread?.userUid, propertyId: String(activeThread?.propertyId || propertyId || '') });
    if (!activeThread) return;
    const pid = String(activeThread.propertyId || propertyId || "");
    const uid = activeThread.userUid || (activeThread.id ? String(activeThread.id).split(":").pop() : undefined);
    if (!pid || !uid) return;

    try {
      dbg('joinThreadRoom ->', { pid, uid, room: `concierge:${pid}:${uid}` });
      joinThreadRoom?.(pid, uid);
      // Proactively clear staff unread on room activation (also triggers /seen via store logic)
      try {
        const st = useConciergeStore.getState();
        if (st && typeof st.markRead === 'function') {
          st.markRead(pid, uid);
          try { apiMarkRead({ propertyId: pid, userUid: uid, actor: resolveStaffActor() }); } catch {}
        }
      } catch (e) {
        console.warn('[ConciergeCenter] markRead on activate failed', e);
      }
      // Also push a concrete /seen for the latest guest message (so Mongo updates seenBy[])
      try {
        const mid = pickLastGuestUnseenId(pid, uid);
        if (mid) {
          apiMarkSeen({ propertyId: pid, userUid: uid, messageId: mid, actor: resolveStaffActor() });
        }
      } catch (e) {
        try { console.warn('[ConciergeCenter] markMessageSeen on activate failed', e); } catch {}
      }
      // console.debug("[ConciergeCenter] joined room", `concierge:${pid}:${uid}`);
    } catch (e) {
      console.warn("[ConciergeCenter] joinThreadRoom failed", e);
    }

    return () => {
      try {
        dbg('leaveThreadRoom ->', { pid, uid, room: `concierge:${pid}:${uid}` });
        leaveThreadRoom?.(pid, uid);
        // console.debug("[ConciergeCenter] left room", `concierge:${pid}:${uid}`);
      } catch {}
    };
  }, [activeThread, propertyId, joinThreadRoom, leaveThreadRoom]);

  // Listen for server read‑receipt events and forward to the store (if handler exists)
  useEffect(() => {
    const s = (typeof window !== 'undefined') ? window.socket : undefined;
    if (!s) return;

    const onSeen = (payload) => {
      try {
        console.debug('[ConciergeCenter] messages:seen', payload);
        const st = useConciergeStore.getState();
        // Prefer a dedicated handler if present
        if (typeof st.applySeen === 'function') return st.applySeen(payload);
        if (typeof st.mergeSeen === 'function') return st.mergeSeen(payload);
        if (typeof st.onMessagesSeen === 'function') return st.onMessagesSeen(payload);
        // Fallback: no-op; other listeners (e.g., MessageList) may react to the socket directly
      } catch (e) {
        try { console.warn('[ConciergeCenter] onSeen handler failed', e); } catch {}
      }
    };

    s.on('messages:seen', onSeen);
    return () => {
      try { s.off('messages:seen', onSeen); } catch {}
    };
  }, [activeThread?.propertyId, activeThread?.userUid]);

  // Auto mark latest incoming (guest) message as seen when it becomes visible
  useEffect(() => {
    const pid = String(activeThread?.propertyId || propertyId || '');
    const uid = activeThread?.userUid || (activeThread?.id ? String(activeThread.id).split(':').pop() : undefined);
    if (!pid || !uid) return;

    // 1) Immediately attempt a logical markRead (picks the latest guest msg id and calls markSeen)
    try {
      const st = useConciergeStore.getState();
      if (st && typeof st.markRead === 'function') {
        st.markRead(pid, uid);
        try { apiMarkRead({ propertyId: pid, userUid: uid, actor: resolveStaffActor() }); } catch {}
      }
    } catch (e) { try { console.warn('[ConciergeCenter] markRead (auto) failed', e); } catch {} }

    // Push a concrete /seen now as well (best-effort); observer below is a safety net
    try {
      const mid = pickLastGuestUnseenId(pid, uid);
      if (mid) {
        apiMarkSeen({ propertyId: pid, userUid: uid, messageId: mid, actor: resolveStaffActor() });
      }
    } catch {}

    // 2) Then arm a visibility observer for the tail bubble to guarantee /seen when it enters view
    let detach = null;
    const arm = () => {
      try {
        const container = document.querySelector('#concierge-main-content');
        if (!container) return;
        const bubbles = container.querySelectorAll('[data-role="guest"][data-message-id]');
        if (!bubbles || !bubbles.length) return;
        const last = bubbles[bubbles.length - 1];
        const midAttr = last.getAttribute('data-message-id');
        if (!midAttr) return;
        const mid = String(midAttr);
        const io = new IntersectionObserver((entries) => {
          const e = entries[0];
          if (e && e.isIntersecting) {
            try { apiMarkSeen({ propertyId: pid, userUid: uid, messageId: mid, actor: resolveStaffActor() }); } catch {}
            io.disconnect();
          }
        }, { root: container, threshold: 0.5 });
        io.observe(last);
        detach = () => io.disconnect();
      } catch (e) { try { console.warn('[ConciergeCenter] IO arm failed', e); } catch {} }
    };
    const t = setTimeout(arm, 120); // allow DOM to paint

    return () => { clearTimeout(t); if (typeof detach === 'function') detach(); };
  }, [activeThread?.id, activeThread?.propertyId, activeThread?.userUid, propertyId, messagesCount]);

  const handleThreadClick = (thread) => {
    dbg('handleThreadClick', { id: thread?.id, userUid: thread?.userUid, propertyId: String(thread?.propertyId || '') });
    setActiveThread(thread);
    const pid = String(thread?.propertyId || propertyId || "");
    const uid = thread?.userUid || (thread?.id ? String(thread.id).split(":").pop() : undefined);
    if (pid && uid) {
      dbg('joinThreadRoom (click) ->', { pid, uid, room: `concierge:${pid}:${uid}` });
      try { 
        joinThreadRoom?.(pid, uid); 
      } catch {}

      // Also mark latest incoming as seen on manual thread selection
      try {
        const st = useConciergeStore.getState();
        if (st && typeof st.markRead === 'function') {
          st.markRead(pid, uid);
          try { apiMarkRead({ propertyId: pid, userUid: uid, actor: resolveStaffActor() }); } catch {}
        }
      } catch (e) {
        console.warn('[ConciergeCenter] markRead on select failed', e);
      }
      try {
        const mid = pickLastGuestUnseenId(pid, uid);
        if (mid) {
          apiMarkSeen({ propertyId: pid, userUid: uid, messageId: mid, actor: resolveStaffActor() });
        }
      } catch (e) {
        try { console.warn('[ConciergeCenter] markMessageSeen on select failed', e); } catch {}
      }
    }
  };

  // Side panel state
  const [sideTab, setSideTab] = useState("inbox");

  const railRef = useRef(null);
  const drawerRef = useRef(null);
  const mainRef = useRef(null);
  const sideRef = useRef(null);
  const drawerPaperRef = useRef(null);
  const mainContentRef = useRef(null);

  try {
    console.groupCollapsed('%c[ConciergeCenter] render', 'color:#2563eb');
    console.log('sideTab:', sideTab);
    console.log('activeThread:', activeThread && { id: activeThread.id || activeThread._id, userUid: activeThread.userUid, propertyId: String(activeThread.propertyId || '') });
    console.log('threads.len:', Array.isArray(threadList) ? threadList.length : 0);
    console.groupEnd();
  } catch {}

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <Box
        ref={railRef}
        sx={{
          width: 64,
          height: '100vh',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          position: 'relative',
        }}
      >
        <ConciergeNavRail active={sideTab} onChange={(id) => setSideTab(id)} />
      </Box>
      {sideTab === "settings" ? (
        // SETTINGS MODE: show SettingsList (which includes the right-hand SettingsPage)
        <Box sx={{ display: "flex", flexGrow: 1, height: "100vh" }}>
          <SettingsList />
        </Box>
      ) : (
        // INBOX MODE: show Threads drawer + chat room (+ optional side panel for other tabs)
        <>
          <Drawer
            variant="permanent"
            ref={drawerRef}
            PaperProps={{
              ref: drawerPaperRef,
              sx: {
                width: drawerWidth,
                boxSizing: 'border-box',
                position: 'relative',
              },
            }}
            sx={{
              width: drawerWidth,
              flexShrink: 0,
            }}
          >
           
            <Divider />
            <ConciergeThreadList
              propertyId={propertyId}
              onSelect={handleThreadClick}
              height={`calc(100% - 64px)`}
            />
          </Drawer>
          <Box sx={{ display: "flex", flexGrow: 1, height: "100vh", position: 'relative' }} ref={mainRef}>
            <Box
              id="concierge-main-content"
              ref={mainContentRef}
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                borderRight: sideTab !== "inbox" ? "1px solid #E5E7EB" : "none",
              }}
            >
              <ConciergeRoom propertyId={propertyId} />
            </Box>
            {sideTab !== "inbox" && (
              <Box ref={sideRef} sx={{ display: 'flex', position: 'relative' }}>
                <SidePanel activeTab={sideTab} onSelect={setSideTab} />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
